import React from 'react';
import { C, Explainer, Tick, rise, type ExplainerProps, type SceneDef, type SceneProps, type Unit } from './engine';

const FIRST = 550;
const GAP = 480;
const T = { ready: FIRST + 6 * GAP + 250, approve: FIRST + 6 * GAP + 650 };

function Thumb({ v }: { v: 0 | 1 }) {
  return (
    <svg width="40" height="28" viewBox="0 0 40 28" style={{ borderRadius: 4, display: 'block' }}>
      <rect width="40" height="28" fill={v ? '#C9CCD4' : '#B9BDC7'} />
      {v === 0 ? (
        <>
          <rect x="0" y="18" width="40" height="10" fill="#34373F" />
          <path d="M8 18 L 20 7 L 31 11" stroke="#5A5E68" strokeWidth="3" fill="none" strokeLinecap="round" />
          <circle cx="20" cy="7" r="2.5" fill="#E2E4E9" />
        </>
      ) : (
        <>
          <circle cx="20" cy="14" r="8" fill="#5A5E68" />
          <circle cx="20" cy="14" r="3" fill="#C9CCD4" />
          <path d="M26 6 L 33 2" stroke="#A8352B" strokeWidth="2" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}


const fieldsFor = (u: Unit): { k: string; src: string; v: React.ReactNode }[] => [
  { k: 'Unit', src: 'ERP', v: <span style={{ whiteSpace: 'nowrap' }}>{u.model}&nbsp;·&nbsp;<span className="xp-mono" style={{ fontSize: 9 }}>{u.serial}</span></span> },
  { k: 'Owner', src: 'Registration', v: u.owner },
  { k: u.Role, src: 'Handover', v: u.partner },
  {
    k: 'Photos',
    src: 'Owner',
    v: (
      <span className="xp-row" style={{ gap: 5 }}>
        <Thumb v={0} />
        <Thumb v={1} />
      </span>
    ),
  },
  { k: 'Service', src: 'Service log', v: u.serviceNote },
  { k: u.statusRow[0], src: 'Handover', v: <span style={{ color: C.emerald }}>{u.since}</span> },
];

function ClaimScene({ t, u }: SceneProps) {
  const FIELDS = fieldsFor(u);
  const at = (ms: number) => t >= ms;
  const n = FIELDS.filter((_, i) => at(FIRST + i * GAP)).length;
  const ready = at(T.ready);
  return (
    <div className="xp-abs xp-card" style={{ left: 56, top: 14, width: 368, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px 12px' }}>
        <div className="xp-row" style={{ justifyContent: 'space-between' }}>
          <span className="xp-cap" style={{ fontSize: 8, color: C.muted }}>
            {u.statusRow[0] === 'Warranty' ? 'Warranty claim' : 'Report'} · Case 2291
          </span>
          <span
            className="xp-mono"
            style={{
              fontSize: 7.5,
              fontWeight: 600,
              letterSpacing: '.1em',
              padding: '3px 8px',
              borderRadius: 20,
              background: ready ? 'rgba(63,174,108,.14)' : C.alt,
              color: ready ? C.emerald : C.muted,
              transition: 'background .2s, color .2s',
            }}
          >
            {ready ? 'COMPLETE' : 'COLLECTING'}
          </span>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-.02em', marginTop: 4 }}>{u.issue}</div>
        <div style={{ fontSize: 9.5, color: C.muted, marginTop: 1 }}>Opened by the owner, from a scan of the serial plate</div>
      </div>
      {FIELDS.map((f, i) => {
        const on = at(FIRST + i * GAP);
        return (
          <div key={f.k} className="xp-row" style={{ height: 34, padding: '0 16px', borderTop: `1px solid ${C.line}`, fontSize: 10.5, fontWeight: 600 }}>
            <span className="xp-cap" style={{ width: 70, fontSize: 7.5, color: C.muted, flex: 'none' }}>
              {f.k}
            </span>
            <span style={{ position: 'relative', flex: 1, height: '100%' }} className="xp-row">
              <span className="xp-abs xp-f" style={{ left: 0, top: 13, height: 7, width: 90 + ((i * 37) % 50), borderRadius: 4, background: '#EEEDF5', opacity: on ? 0 : 1 }} />
              <span className="xp-a xp-row" style={{ ...rise(on, 0, 6) }}>
                {f.v}
              </span>
            </span>
            <span className="xp-row xp-a" style={{ ...rise(on, 120, 4), gap: 5, flex: 'none' }}>
              <span className="xp-mono" style={{ fontSize: 7, fontWeight: 500, letterSpacing: '.08em', color: C.muted, textTransform: 'uppercase' }}>
                {f.src}
              </span>
              <span className="xp-row" style={{ width: 13, height: 13, borderRadius: 13, justifyContent: 'center', background: C.emerald }}>
                <Tick on={on} size={9} />
              </span>
            </span>
          </div>
        );
      })}
      <div className="xp-row" style={{ height: 54, padding: '0 16px', gap: 12, borderTop: `1px solid ${C.line}`, background: C.alt }}>
        <span style={{ flex: 1 }}>
          <span className="xp-mono" style={{ display: 'block', fontSize: 8, color: C.muted, letterSpacing: '.06em' }}>
            {n} OF 6 ATTACHED
          </span>
          <span style={{ display: 'block', marginTop: 5, height: 4, borderRadius: 4, background: C.line, overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${(n / 6) * 100}%`, background: C.grad, transition: 'width .35s var(--xe)' }} />
          </span>
        </span>
        <span
          className="xp-row"
          style={{
            height: 32,
            padding: '0 14px',
            gap: 6,
            borderRadius: 32,
            fontSize: 10.5,
            fontWeight: 700,
            background: ready ? C.grad : '#E9E8F2',
            color: ready ? C.indigo : '#9A98B8',
            transform: at(T.approve) ? 'scale(1.04)' : 'none',
            boxShadow: at(T.approve) ? '0 8px 18px -8px rgba(63,174,108,.7)' : 'none',
            transition: 'transform .3s var(--xe), box-shadow .3s',
          }}
        >
          {ready ? 'Ready to approve' : 'Waiting for details'}
        </span>
      </div>
    </div>
  );
}

export const claimDef: SceneDef = {
  name: 'Claim',
  label: (u) => `A claim on a ${u.brand} ${u.title} assembles itself: unit and serial from the ERP, owner from registration, ${u.partnerRole} and ${u.statusRow[0].toLowerCase()} from the handover, two photos from the owner and the service history. It arrives complete and ready to approve.`,
  W: 480,
  H: 360,
  duration: 8200,
  end: 7600,
  steps: [
    { at: 0, label: 'Claim opened by a scan' },
    { at: FIRST + 3 * GAP, label: 'Details attach themselves' },
    { at: T.ready, label: 'Ready to approve' },
  ],
  Scene: ClaimScene,
};

export const ClaimExplainer = (p: ExplainerProps) => <Explainer def={claimDef} {...p} />;
